import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowsUpDownIcon, ArrowLongUpIcon, ArrowLongDownIcon } from '@/components/Icons';
import cn from '@/utils/cn';
import LoadingComponent from '@/components/Loading';
import DataTableFooter from './DataTableFooter';
import { useTableOverflow } from './useTableOverflow';
import { CheckboxCell, CheckboxCellClass } from './CheckboxCell';
import {
  SortDirectionEnum,
  TTableColumn,
  TTablePagination,
  TTableSelection,
  TTableSort,
  TTableSorting
} from './types';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';

const CellClass = cn(['pl-4', 'pr-6', 'py-4']);
const CheckboxClass = cn([
  'w-4',
  'h-4',
  'rounded',
  'border-emerge-navy-4',
  'text-emerge-teal',
  'focus:ring-transparent',
  'cursor-pointer'
]);
const hiddenStyleOnSmallScreens = 'tablet:hidden mobile:hidden';
// Matches CheckboxCellClass ('p-4 w-4' = 16px content + 16px padding each side).
const SELECTION_COLUMN_WIDTH = '48px';

interface HeaderCheckboxProps {
  checked: boolean;
  onChange: () => void;
}

// Deliberately binary, no indeterminate/"some selected" visual - checked only when every
// selectable row is, plain unchecked for both "some" and "none" selected. (An earlier version
// showed a dash for "some selected" via the DOM `.indeterminate` property, but that still read as
// a colored/"on" header to users at a glance - product direction is a strict "only checked when
// everything is" rule instead.)
const HeaderCheckbox: React.FC<HeaderCheckboxProps> = ({ checked, onChange }) => (
  <input
    type="checkbox"
    checked={checked}
    onChange={onChange}
    aria-label="Select all rows"
    className={CheckboxClass}
  />
);

interface DataTableProps<TRow> {
  columns: TTableColumn<TRow>[];
  data: TRow[];
  getRowKey: (row: TRow, index: number) => string | number;
  pagination?: TTablePagination;
  sorting?: TTableSorting;
  isLoading?: boolean;
  emptyMessage?: ReactNode;
  loadingMessage?: ReactNode;
  /** Footer label for the item count, e.g. "Transactions" / "Statements". */
  itemLabel?: string;
  onRowClick?: (row: TRow) => void;
  /**
   * Highlights a row the same way a checkbox-selected row is (bg-emerge-teal-5, no hover tint) -
   * for a row that's "active" for a reason other than checkbox selection, e.g. currently open in
   * a details drawer via onRowClick. Matches TransactionHistoryTableRow's isSelected treatment.
   * Independent of `selection` - a row can be active, checkbox-selected, both, or neither.
   */
  isRowActive?: (row: TRow, index: number) => boolean;
  /** When supplied, a row for which this returns false renders its checkbox disabled (and
   *  unchecked) instead of interactive - for rows that exist in the list but can never actually
   *  be acted on by whatever bulk action the selection drives. Defaults to every row being
   *  selectable, unchanged from before this prop existed. */
  isRowSelectable?: (row: TRow, index: number) => boolean;
  /**
   * Full-row escape hatch for complex rows. When supplied, columns are used only
   * for the header/sorting; each row is rendered with this instead of `column.cell`.
   */
  renderRow?: (row: TRow, index: number) => ReactNode;
  /** Adds a leading checkbox column with header select-all support. */
  selection?: TTableSelection<TRow>;
  /** Disables the sticky/floating footer behaviour. */
  disableStickyFooter?: boolean;
  /**
   * Background behind the footer while it's floating/sticky (covers row content scrolling
   * underneath it). Defaults to white; override when the page behind the table isn't white.
   */
  floatingFooterBackgroundClassName?: string;
  /**
   * Horizontal alignment of `emptyMessage`. Defaults to 'center' (unchanged from before this
   * prop existed - e.g. Selected Account's tables). Manage Payment's tables want 'left', per
   * Figma's empty-state row (plain left-aligned text, not a centered placeholder block).
   */
  emptyMessageAlign?: 'center' | 'left';
  /**
   * Whether the empty state reserves the same tall min-height as a populated table
   * (min-h-[14rem] outer / min-h-[20rem] inner). Defaults to true, unchanged from before this
   * prop existed. Manage Payment's tables want false - Figma's empty row is a single compact
   * line with normal padding, not a large centered placeholder area.
   */
  emptyStateFixedHeight?: boolean;
  /**
   * Caps the table body's height and scrolls only the rows within it, same spirit as Manage
   * Payment's tables (`emptyStateFixedHeight={false}`, no forced placeholder area) but for a
   * table that also needs an upper bound: it's a `max-height`, not a fixed size, so it takes up
   * only as much room as the actual row count needs - none extra for an empty/short result,
   * scrolling only once there's enough data to exceed the cap.
   *
   * Unlike a plain `overflow-y-auto` wrapper around the whole table, the header renders as a
   * genuinely separate, non-scrolling `<table>` above the scrolling one, so the scrollbar itself
   * starts below the header row instead of running its full height. The two tables' columns are
   * kept aligned via `table-layout: fixed` and a shared `<colgroup>` - see `TTableColumn.width`
   * to pin a column's width (unpinned columns split whatever's left evenly); this also keeps a
   * narrow table (e.g. inside a modal) from stretching wider than its container, which
   * `table-layout: auto`'s natural, content-driven column widths otherwise can.
   *
   * Must be a literal CSS length ('60vh', '400px') - not a percentage, which would need an
   * ancestor with a matching definite height to resolve against (not supported here). Leave
   * unset for the default: a single table that simply grows with its row count and whatever
   * container it's in scrolls instead.
   */
  maxBodyHeight?: string;
}

/**
 * Splices `item` into `cells` at `position`, clamped to a valid index - used to place the
 * checkbox column anywhere among the data columns (default 0 = leftmost, unchanged from before
 * `TTableSelection.columnPosition` existed). Returns `cells` untouched when there's no item to
 * insert (no `selection` prop given).
 */
const insertAt = <T,>(cells: T[], item: T | null, position: number): T[] => {
  if (item === null) return cells;
  const clampedPosition = Math.min(Math.max(position, 0), cells.length);
  return [...cells.slice(0, clampedPosition), item, ...cells.slice(clampedPosition)];
};

const resolveNextSort = (column: TTableColumn<unknown>, activeSort?: TTableSort): TTableSort => {
  if (activeSort?.key === column.key) {
    return {
      key: column.key,
      direction:
        activeSort.direction === SortDirectionEnum.ASC
          ? SortDirectionEnum.DESC
          : SortDirectionEnum.ASC
    };
  }
  return { key: column.key, direction: SortDirectionEnum.ASC };
};

const DataTable = <TRow,>({
  columns,
  data,
  getRowKey,
  pagination = { mode: 'none' },
  sorting = { mode: 'none' },
  isLoading = false,
  emptyMessage = 'No records to display.',
  itemLabel = 'Records',
  onRowClick,
  isRowActive,
  isRowSelectable,
  renderRow,
  selection,
  disableStickyFooter = false,
  floatingFooterBackgroundClassName,
  emptyMessageAlign = 'center',
  emptyStateFixedHeight = true,
  maxBodyHeight
}: DataTableProps<TRow>) => {
  const [clientSort, setClientSort] = useState<TTableSort | undefined>(
    sorting.mode === 'client' ? sorting.initialSort : undefined
  );
  const [clientPage, setClientPage] = useState(1);
  // Only used in maxBodyHeight mode, to mirror the body's horizontal scroll onto the otherwise
  // non-scrolling header table, so the two stay column-aligned if the table is ever wider than
  // its container too (not just taller).
  const headerScrollRef = useRef<HTMLDivElement>(null);

  const activeSort = sorting.mode === 'server' ? sorting.sort : clientSort;

  // Client-side sort runs against the column's accessor.
  const sortedData = useMemo(() => {
    if (sorting.mode !== 'client' || !clientSort) return data;
    const column = columns.find((c) => c.key === clientSort.key);
    if (!column?.sortAccessor) return data;
    const directionFactor = clientSort.direction === SortDirectionEnum.ASC ? 1 : -1;
    return [...data].sort((a, b) => {
      const aValue = column.sortAccessor!(a);
      const bValue = column.sortAccessor!(b);
      // A row with no value to sort by always sorts after one that does, regardless of
      // direction - deliberately NOT multiplied by directionFactor, otherwise reversing the
      // sort would flip it to the front instead of keeping it last.
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      if (aValue < bValue) return -1 * directionFactor;
      if (aValue > bValue) return 1 * directionFactor;
      return 0;
    });
  }, [data, columns, clientSort, sorting.mode]);

  const totalItemsCount =
    pagination.mode === 'server' ? pagination.totalItemsCount : sortedData.length;
  const currentPage = pagination.mode === 'server' ? pagination.currentPage : clientPage;

  // Reset to the first page whenever the client-side dataset or sort changes.
  useEffect(() => {
    if (pagination.mode === 'client') setClientPage(1);
  }, [pagination.mode, sortedData.length, clientSort]);

  const visibleData = useMemo(() => {
    if (pagination.mode === 'client') {
      const start = (clientPage - 1) * pagination.pageSize;
      return sortedData.slice(start, start + pagination.pageSize);
    }
    // server mode rows are already sliced upstream; none mode shows everything.
    return sortedData;
  }, [sortedData, pagination, clientPage]);

  const handleHeaderClick = (column: TTableColumn<TRow>) => {
    if (!column.canSort || sorting.mode === 'none') return;
    const next = resolveNextSort(column as TTableColumn<unknown>, activeSort);
    if (sorting.mode === 'client') {
      setClientSort(next);
    } else {
      sorting.onSortChange(next);
    }
  };

  const isEmpty = visibleData.length === 0;

  // The header checkbox's "all"/indeterminate state must only account for rows that can actually
  // be selected - a non-selectable row (e.g. a pending invite, see isRowSelectable) never enters
  // selectedKeys no matter what, so counting it in the denominator would make "all" unreachable
  // whenever one is visible. Indices are kept aligned with the original visibleData position
  // (not the filtered array's own index) since both isRowSelectable and getRowKey are called with
  // that original index elsewhere (the row-rendering loop below).
  const selectableVisibleEntries = visibleData
    .map((row, index) => ({ row, index }))
    .filter(({ row, index }) => (isRowSelectable ? isRowSelectable(row, index) : true));

  const selectedVisibleCount = selection
    ? selectableVisibleEntries.filter(({ row, index }) =>
        selection.selectedKeys.has(getRowKey(row, index))
      ).length
    : 0;
  const isAllSelected =
    !isEmpty &&
    selectableVisibleEntries.length > 0 &&
    selectedVisibleCount === selectableVisibleEntries.length;

  const { containerRef, isOverflowing } = useTableOverflow<HTMLDivElement>();
  const useNewFooter = isFeatureEnabled(Feature.SELECTED_ACCOUNT);
  const hasFooter = pagination.mode !== 'none';
  const isFloatingFooter = !disableStickyFooter && useNewFooter && hasFooter && isOverflowing;

  // Shared between the single-table (default) and split header/body tables (maxBodyHeight) -
  // kept in one place so the two layouts can never drift apart on cell markup.
  const headerCheckboxCell = selection ? (
    <th scope="col" className={CheckboxCellClass} key="header-checkbox">
      <HeaderCheckbox checked={isAllSelected} onChange={selection.onToggleAll} />
    </th>
  ) : null;

  const headerColumnCells = columns.map((column) => (
    <th
      scope="col"
      className={cn(
        CellClass,
        'text-emerge-navy-2 text-xs font-medium',
        column.hideOnSmallScreens ? hiddenStyleOnSmallScreens : null,
        column.thClassName
      )}
      key={`header-${column.key}`}>
      <div className="flex flex-row items-center gap-x-1">
        {column.header}
        {column.canSort && sorting.mode !== 'none' && (
          <button
            type="button"
            onClick={() => handleHeaderClick(column)}
            aria-label={
              activeSort?.key === column.key
                ? `Sort by ${column.key} (${activeSort.direction === SortDirectionEnum.ASC ? 'ascending' : 'descending'})`
                : `Sort by ${column.key}`
            }
            className="cursor-pointer">
            {/* The active sort column shows its actual current direction
                (a single up or down arrow), not the ambiguous
                both-directions selector - that stays for every other
                sortable column, which isn't currently sorted by either
                direction. size="xxs" (12px) matches Figma and the header
                text's own line-height - the icon default (20px) towered
                over the 12px header label. */}
            {activeSort?.key === column.key ? (
              activeSort.direction === SortDirectionEnum.ASC ? (
                <ArrowLongUpIcon size="xxs" />
              ) : (
                <ArrowLongDownIcon size="xxs" />
              )
            ) : (
              <ArrowsUpDownIcon size="xxs" />
            )}
          </button>
        )}
      </div>
    </th>
  ));

  const headerRow = (
    <tr>{insertAt(headerColumnCells, headerCheckboxCell, selection?.columnPosition ?? 0)}</tr>
  );

  const bodyContent = isLoading ? (
    <tr>
      <td colSpan={columns.length + (selection ? 1 : 0)}>
        <div className="flex items-center justify-center min-h-[20rem] text-center">
          <LoadingComponent />
        </div>
      </td>
    </tr>
  ) : isEmpty ? (
    <tr>
      <td colSpan={columns.length + (selection ? 1 : 0)}>
        <div
          className={cn(
            'flex items-center',
            emptyMessageAlign === 'left' ? 'justify-start text-left' : 'justify-center text-center',
            emptyStateFixedHeight ? 'min-h-[20rem]' : 'p-4'
          )}>
          <p className="text-emerge-navy-2 font-medium">{emptyMessage}</p>
        </div>
      </td>
    </tr>
  ) : (
    visibleData.map((row, index) => {
      const isRowSelected = selection?.selectedKeys.has(getRowKey(row, index)) ?? false;
      const isHighlighted = isRowSelected || (isRowActive?.(row, index) ?? false);

      const checkboxCell = selection ? (
        <td className={CheckboxCellClass} key="cell-checkbox">
          <CheckboxCell
            checked={selection.selectedKeys.has(getRowKey(row, index))}
            onChange={() => selection.onToggleRow(row, index)}
            disabled={isRowSelectable ? !isRowSelectable(row, index) : false}
            ariaLabel="Select row"
          />
        </td>
      ) : null;

      const columnCells = columns.map((column) => (
        <td
          key={`cell-${column.key}`}
          className={cn(
            CellClass,
            'text-dark-navy-30 text-xs font-medium',
            column.hideOnSmallScreens ? hiddenStyleOnSmallScreens : null,
            column.tdClassName
          )}>
          {column.cell?.(row, index)}
        </td>
      ));

      return renderRow ? (
        <React.Fragment key={getRowKey(row, index)}>{renderRow(row, index)}</React.Fragment>
      ) : (
        <tr
          key={getRowKey(row, index)}
          onClick={
            onRowClick
              ? () => onRowClick(row)
              : selection
                ? () => selection.onToggleRow(row, index)
                : undefined
          }
          className={cn(
            // 'group' is otherwise inert here - it only matters to a cell that opts in with
            // `group-hover:`, e.g. Cards' grouped-row hover reveal for a member's non-first card.
            'group border-b transition-colors',
            isHighlighted ? 'bg-emerge-teal-5' : 'bg-white',
            (onRowClick || selection) && 'hover:cursor-pointer',
            (onRowClick || selection) && !isHighlighted && 'hover:bg-emerge-base-2'
          )}>
          {insertAt(columnCells, checkboxCell, selection?.columnPosition ?? 0)}
        </tr>
      );
    })
  );

  // Only meaningful in maxBodyHeight mode: the same explicit widths, in the same order, given to
  // BOTH the header-only and body-only <table>s - `table-layout: fixed` then sizes matching
  // columns identically in each, with no JS measurement needed (an unset width just divides the
  // remaining space evenly, the same way in both tables, as long as both render at 100% of the
  // same container width).
  const selectionCol = selection ? (
    <col style={{ width: SELECTION_COLUMN_WIDTH }} key="col-checkbox" />
  ) : null;

  const columnCols = columns.map((column) => (
    <col key={`col-${column.key}`} style={column.width ? { width: column.width } : undefined} />
  ));

  const colgroup = (
    <colgroup>{insertAt(columnCols, selectionCol, selection?.columnPosition ?? 0)}</colgroup>
  );

  return (
    <>
      {/* OUTER: handles radius + border */}
      <div ref={containerRef} className="relative">
        {/*
          The footer renders as a sibling, not a child, of this box: `overflow-hidden` (needed
          to clip the table's square corners under the rounded border) would otherwise become
          the footer's sticky containing block, trapping it and preventing it from ever
          visually sticking to the page's real scroll container.
        */}
        <div
          className={cn(
            'border-gray-200 overflow-hidden bg-white',
            // Only round the top when a footer continues the shape below (the footer draws
            // its own bottom corners/border) - with no footer, this box is the whole visible
            // container, so it needs all four corners rounded, not just the top two.
            hasFooter ? 'rounded-t-2xl border-t border-l border-r' : 'rounded-2xl border',
            isEmpty && emptyStateFixedHeight ? 'min-h-[14rem]' : ''
          )}>
          {maxBodyHeight ? (
            <>
              {/* Fixed header - a separate, non-scrolling table, so the scrollbar below
                  starts at the top of the rows instead of running the table's full height.
                  `overflow-y-scroll` (not visible/hidden) + the same `vertical-scroll` custom
                  styling as the body below reserves an identical vertical scrollbar gutter here
                  too, even though nothing ever actually scrolls in this table - otherwise, once
                  the body's real scrollbar appears, its content area is narrower than the
                  header's by that gutter width, and a `table-layout: fixed` column left unset
                  (sized as "whatever's left") would compute a different width in each table,
                  shifting the boundary between columns out of alignment with the header above. */}
              <div
                ref={headerScrollRef}
                className="overflow-x-hidden overflow-y-scroll vertical-scroll">
                <table className="w-full text-sm text-left" style={{ tableLayout: 'fixed' }}>
                  {colgroup}
                  <thead className="text-xs font-medium text-emerge-navy-2 bg-emerge-base-3">
                    {headerRow}
                  </thead>
                </table>
              </div>
              {/* Scrollable body - the only thing maxBodyHeight actually bounds, and only a
                  `max-height`: an empty/short result just renders at its own natural (small)
                  height, no forced blank space below it - it only scrolls once real row count
                  grows past the cap. `overflow-y-scroll` (not `auto`) so the vertical gutter is
                  always reserved too, matching the header above regardless of whether this
                  particular result actually needs to scroll - see the header's own comment. */}
              <div
                className="overflow-y-scroll overflow-x-auto vertical-scroll text-gray-500"
                style={{ maxHeight: maxBodyHeight }}
                onScroll={(e) => {
                  if (headerScrollRef.current) {
                    headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
                  }
                }}>
                <table className="w-full text-sm text-left" style={{ tableLayout: 'fixed' }}>
                  {colgroup}
                  <tbody>{bodyContent}</tbody>
                </table>
              </div>
            </>
          ) : (
            /* INNER: keeps your overflow behavior intact - the default, unbounded table. */
            <div className="overflow-x-auto text-gray-500">
              <table className="w-full text-sm text-left">
                <thead className="sticky z-5 top-0 text-xs font-medium text-emerge-navy-2 bg-emerge-base-3">
                  {headerRow}
                </thead>
                <tbody>{bodyContent}</tbody>
              </table>
            </div>
          )}
        </div>
        {hasFooter && (
          <DataTableFooter
            itemLabel={itemLabel}
            onPageChange={pagination.mode === 'server' ? pagination.onPageChange : setClientPage}
            currentPage={currentPage}
            totalItemsCount={totalItemsCount}
            pageSize={pagination.pageSize}
            currentPageItemsCount={visibleData.length}
            isFloating={isFloatingFooter}
            floatingBackgroundClassName={floatingFooterBackgroundClassName}
          />
        )}
      </div>
    </>
  );
};

export default DataTable;
